import { WSText } from "@wingspanhq/fe-component-library";
import { Route, useHistory } from "react-router-dom";
import { OverlaySpinner } from "../../components/OverlaySpinner";
import { SetupFlow } from "../../components/SetupFlow/SetupFlow";
import { useWSMutation } from "../../query/helpers";
import { useUserId } from "../../query/hooks/helpers";
import { QUERY_USERS_ACTIVITIES } from "../../query/users/keys";
import { useMemberProfile } from "../../query/users/queries";
import { WSQueries } from "../../query/WSQuery";
import { usersService } from "../../services/users";
import { useWSAnalytics } from "../../utils/WSAnalytics";
import { CalculationSettings } from "./CalculationSettings";
import { ConfirmPayerInfo } from "./ConfirmPayerInfo";
import { GenerateForms } from "./GenerateForms";
import { NEC1099FilingImportCollaborators } from "./NEC1099FilingImportCollaborators";
import { NEC1099FilingImportCollaboratorsQuestion } from "./NEC1099FilingImportCollaboratorsQuestion";
import { NEC1099FilingMissingInfo } from "./NEC1099FilingMissingInfo";
import { NEC1099FilingOffPlatformPayments } from "./NEC1099FilingOffPlatformPayments";
import { NEC1099FilingUploadCollaborators } from "./NEC1099FilingUploadCollaborators";
import { NEC1099FilingUploadFailedCollaborators } from "./NEC1099FilingUploadFailedCollaborators";
import { NEC1099FilingUploadFailedOffPlatformPayments } from "./NEC1099FilingUploadFailedOffPlatformPayments";
import { NEC1099FilingUploadOffPlatformPayments } from "./NEC1099FilingUploadOffPlatformPayments";
import { OffPlatformPaymentsQuestion } from "./OffPlatformPaymentsQuestion";
import { StateFiling } from "./StateFiling";

type Setup1099Props = {
  basePath: string;
  onBack: () => Promise<void> | void;
  onFinish: () => Promise<void> | void;
};
export const Setup1099: React.FC<Setup1099Props> = ({
  basePath,
  onBack,
  onFinish
}) => {
  const history = useHistory();
  const userId = useUserId();
  const memberQuery = useMemberProfile(userId);
  const { track } = useWSAnalytics();

  const [finishOnboarding, finishOnboardingMeta] = useWSMutation(
    async () => {
      await usersService.activity.update(userId, {
        flows: {
          nec1099Setup: {
            version: 1,
            currentStep: 5,
            totalSteps: 5,
            complete: true
          }
        }
      });

      track.other("Product activation");

      await onFinish();
    },
    {
      dependencies: [QUERY_USERS_ACTIVITIES]
    }
  );

  return (
    <WSQueries queries={{ memberQuery }}>
      {({ memberQuery: { data: member } }) => {
        return (
          <>
            {finishOnboardingMeta.isLoading && <OverlaySpinner />}

            <SetupFlow
              basePath={basePath}
              routes={[
                {
                  path: "/confirm-payer-info",
                  component: ConfirmPayerInfo,
                  onBack: onBack,
                  onNext: () => {
                    history.push(basePath + "/select-import-collaborators");
                  }
                },
                {
                  path: "/select-import-collaborators",
                  component: NEC1099FilingImportCollaboratorsQuestion,
                  onBack: () => {
                    history.push(basePath + "/confirm-payer-info");
                  },
                  onNext: context => {
                    if (context?.shouldImportCollaborators) {
                      history.push(basePath + "/import-collaborators");
                    } else if (context?.doMissingInfoCollaboratorsExists) {
                      history.push(basePath + "/missing-info");
                    } else {
                      history.push(basePath + "/select-off-platform-payments");
                    }
                  }
                },
                {
                  path: "/import-collaborators",
                  component: NEC1099FilingImportCollaborators,
                  onBack: () => {
                    history.push(basePath + "/select-import-collaborators");
                  },
                  onNext: context => {
                    history.replace({
                      pathname: basePath + "/upload-collaborators",
                      state: {
                        bulkCollaboratorBatchId:
                          context?.bulkCollaboratorBatchId,
                        csvCollaborators: context?.csvCollaborators,
                        selectedCollaboratorGroup:
                          context?.selectedCollaboratorGroup,
                        fileName: context?.fileName,
                        flatFileBatchId: context?.batchId
                      }
                    });
                  }
                },
                {
                  path: "/upload-collaborators",
                  component: NEC1099FilingUploadCollaborators,
                  onBack: () => {
                    history.push(basePath + "/import-collaborators");
                  },
                  onNext: context => {
                    if (context?.doMissingInfoCollaboratorsExists) {
                      history.push(basePath + "/missing-info");
                    } else {
                      history.push(basePath + "/select-off-platform-payments");
                    }
                  }
                },
                {
                  path: "/upload-failed-collaborators/batch/:batchId",
                  component: NEC1099FilingUploadFailedCollaborators,
                  onBack: () => {
                    history.push(basePath + "/import-collaborators");
                  },
                  onNext: context => {
                    if (context?.doMissingInfoCollaboratorsExists) {
                      history.push(basePath + "/missing-info");
                    } else {
                      history.push(basePath + "/select-off-platform-payments");
                    }
                  }
                },
                {
                  path: "/missing-info",
                  component: NEC1099FilingMissingInfo,
                  onBack: () => {
                    history.push(basePath + "/import-collaborators");
                  },
                  onNext: context => {
                    if (context?.skipForNow) {
                      history.push(basePath + "/select-off-platform-payments");
                    } else {
                      history.replace({
                        pathname: basePath + "/upload-collaborators",
                        state: {
                          bulkCollaboratorBatchId:
                            context?.bulkCollaboratorBatchId,
                          csvCollaborators: context?.csvCollaborators,
                          selectedCollaboratorGroup:
                            context?.selectedCollaboratorGroup,
                          fileName: context?.fileName,
                          flatFileBatchId: context?.batchId
                        }
                      });
                    }
                  }
                },
                {
                  path: "/select-off-platform-payments",
                  component: OffPlatformPaymentsQuestion,
                  onBack: () => {
                    history.push(basePath + "/import-collaborators");
                  },
                  onNext: context => {
                    if (context?.shouldImportOffPlatformPayments) {
                      history.push(basePath + "/off-platform-payments");
                    } else {
                      history.push(basePath + "/calculation-settings");
                    }
                  }
                },
                {
                  path: "/off-platform-payments",
                  component: NEC1099FilingOffPlatformPayments,
                  onBack: () => {
                    history.push(basePath + "/select-off-platform-payments");
                  },
                  onNext: context => {
                    history.push({
                      pathname: basePath + "/upload-off-platform-payments",
                      state: {
                        lineItems: context?.lineItems,
                        fileName: context?.fileName,
                        bulkPayableBatchId: context?.bulkPayableBatchId,
                        flatFileBatchId: context?.flatFileBatchId
                      },
                      search: context?.search
                    });
                  }
                },
                {
                  path: "/upload-off-platform-payments",
                  component: NEC1099FilingUploadOffPlatformPayments,
                  onBack: () => {
                    history.push(basePath + "/off-platform-payments");
                  },
                  onNext: () => {
                    history.push(basePath + "/calculation-settings");
                  }
                },
                {
                  path: "/upload-failed-off-platform-payments/batch/:batchId",
                  component: NEC1099FilingUploadFailedOffPlatformPayments,
                  onBack: () => {
                    history.push(basePath + "/off-platform-payments");
                  },
                  onNext: () => {
                    history.push(basePath + "/calculation-settings");
                  }
                },
                {
                  path: "/calculation-settings",
                  component: CalculationSettings,
                  onBack: () => {
                    history.push(basePath + "/off-platform-payments");
                  },
                  onNext: () => {
                    history.push(basePath + "/state-filing-settings");
                  }
                },
                {
                  path: "/state-filing-settings",
                  component: StateFiling,
                  onBack: () => {
                    history.push(basePath + "/calculation-settings");
                  },
                  onNext: context => {
                    history.push(
                      basePath +
                        `/generate-forms/${context?.bulkCalculation1099BatchId}`
                    );
                  }
                },
                {
                  path: "/generate-forms/:bulkCalculation1099BatchId",
                  component: GenerateForms,
                  onBack: () => {
                    history.push(basePath + "/state-filing-settings");
                  },
                  onNext: () => {
                    finishOnboarding();
                  }
                }
              ]}
            />
          </>
        );
      }}
    </WSQueries>
  );
};
